(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const editor = Boolean(window.Shopify && window.Shopify.designMode);

  class PjHero extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.dots = [...this.querySelectorAll('[data-slide-to]')];
      this.index = Math.max(0, this.slides.findIndex(slide => slide.classList.contains('is-active')));
      this.visible = true;
      this.paused = false;
      const signal = this.controller.signal;
      this.addEventListener('click', event => {
        const dot = event.target.closest('[data-slide-to]');
        if (dot) this.show(Number(dot.dataset.slideTo), true);
        const pause = event.target.closest('[data-pause]');
        if (pause) {
          this.paused = !this.paused;
          pause.setAttribute('aria-pressed', String(this.paused));
          this.schedule();
        }
      }, { signal });
      this.addEventListener('pointerenter', () => { this.hovered = true; this.clear(); }, { signal });
      this.addEventListener('pointerleave', () => { this.hovered = false; this.schedule(); }, { signal });
      this.addEventListener('focusin', () => this.clear(), { signal });
      this.addEventListener('focusout', event => { if (!this.contains(event.relatedTarget)) this.schedule(); }, { signal });
      this.addEventListener('keydown', event => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1), true);
          this.dots[this.index]?.focus();
        }
      }, { signal });
      this.addEventListener('pointerdown', event => { this.startX = event.clientX; this.startY = event.clientY; }, { signal, passive: true });
      this.addEventListener('pointerup', event => {
        const dx = event.clientX - this.startX;
        const dy = event.clientY - this.startY;
        if (Math.abs(dx) > 65 && Math.abs(dx) > Math.abs(dy) * 1.6) this.show(this.index + (dx > 0 ? -1 : 1), true);
      }, { signal, passive: true });
      document.addEventListener('visibilitychange', () => this.schedule(), { signal });
      reduced.addEventListener('change', () => { this.schedule(); this.updateVideo(); }, { signal });
      document.addEventListener('shopify:block:select', event => {
        const selected = event.target.closest('[data-slide]');
        const index = this.slides.indexOf(selected);
        if (index >= 0) this.show(index, true);
      }, { signal });
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.schedule();
        this.updateVideo();
      }, { threshold: 0.05 });
      this.observer.observe(this);
      this.updateVideo();
      this.schedule();
    }
    show(index, user = false) {
      if (!this.slides.length) return;
      this.index = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.index);
        dot.setAttribute('aria-pressed', String(i === this.index));
      });
      if (user) this.paused = true;
      this.updateVideo();
      this.schedule();
    }
    updateVideo() {
      this.slides?.forEach((slide, i) => slide.querySelectorAll('video').forEach(video => {
        video.muted = true;
        video.playsInline = true;
        if (i === this.index && this.visible && !document.hidden && !reduced.matches) video.play().catch(() => {});
        else video.pause();
      }));
      this.slides?.forEach((slide, i) => slide.querySelectorAll('[data-video-src]').forEach(frame => {
        const playing = i === this.index && this.visible && !document.hidden && !reduced.matches;
        if (playing && !frame.hasAttribute('src')) frame.src = frame.dataset.videoSrc;
        if (!frame.hasAttribute('src')) return;
        const message = frame.dataset.videoHost === 'youtube' ? { event: 'command', func: playing ? 'playVideo' : 'pauseVideo', args: [] } : { method: playing ? 'play' : 'pause' };
        const origin = frame.dataset.videoHost === 'youtube' ? 'https://www.youtube.com' : 'https://player.vimeo.com';
        frame.contentWindow?.postMessage(JSON.stringify(message), origin);
      }));
    }
    clear() { window.clearTimeout(this.timer); }
    schedule() {
      this.clear();
      if (this.dataset.autoplay !== 'true' || this.slides.length < 2 || this.paused || this.hovered || !this.visible || document.hidden || reduced.matches || editor || this.matches(':focus-within')) return;
      this.timer = window.setTimeout(() => this.show(this.index + 1), Math.max(4000, Number(this.dataset.delay) || 7000));
    }
    disconnectedCallback() { this.clear(); this.controller?.abort(); this.observer?.disconnect(); }
  }

  class PjContinuous extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.track = this.querySelector('.pj-loop-track');
      this.group = this.querySelector('.pj-loop-group');
      this.viewport = this.querySelector('.pj-loop-viewport');
      this.setup();
      reduced.addEventListener('change', () => this.setup(), { signal });
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-pause]');
        if (!button) return;
        const paused = this.dataset.paused !== 'true';
        this.dataset.paused = String(paused);
        button.setAttribute('aria-pressed', String(paused));
      }, { signal });
      this.viewport?.addEventListener('pointerdown', () => {
        if (!this.classList.contains('is-looping')) return;
        const transform = new DOMMatrix(getComputedStyle(this.track).transform).m41;
        this.classList.remove('is-looping');
        this.viewport.scrollLeft = Math.abs(transform);
      }, { signal, passive: true });
      this.observer = new IntersectionObserver(entries => { this.dataset.outside = String(!entries[0].isIntersecting); }, { rootMargin: '60px' });
      this.observer.observe(this);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.dataset.outside = 'true';
        else this.dataset.outside = String(this.getBoundingClientRect().bottom < 0 || this.getBoundingClientRect().top > window.innerHeight);
      }, { signal });
    }
    setup() {
      if (!this.track || !this.group) return;
      this.track.querySelectorAll('[data-pj-clone]').forEach(clone => clone.remove());
      const animate = this.dataset.auto === 'true' && !reduced.matches && !editor;
      this.classList.toggle('is-looping', animate);
      if (!animate) return;
      const clone = this.group.cloneNode(true);
      clone.dataset.pjClone = '';
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      clone.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
      this.track.append(clone);
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); }
  }

  if (!customElements.get('pj-cinematic-hero')) customElements.define('pj-cinematic-hero', PjHero);
  if (!customElements.get('pj-continuous')) customElements.define('pj-continuous', PjContinuous);

  class PjTrustStrip extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.rail = this.querySelector('.premium-standard__bar');
      this.items = [...this.querySelectorAll('.premium-standard__item')];
      this.stopped = false;
      this.visible = false;
      this.addEventListener('pointerdown', () => { this.stopped = true; this.schedule(); }, { signal, passive: true });
      this.addEventListener('focusin', () => { this.stopped = true; this.schedule(); }, { signal });
      reduced.addEventListener('change', () => this.schedule(), { signal });
      window.addEventListener('resize', () => this.schedule(), { signal, passive: true });
      document.addEventListener('visibilitychange', () => this.schedule(), { signal });
      this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.schedule(); }, { threshold: 0.15 });
      this.observer.observe(this);
    }
    schedule() {
      clearTimeout(this.timer);
      if (this.stopped || !this.visible || document.hidden || reduced.matches || editor || window.innerWidth > 749 || this.dataset.mobileLayout !== 'carousel' || this.dataset.auto !== 'true' || this.items.length < 2) return;
      this.timer = setTimeout(() => {
        const nearest = this.items.reduce((best, item, i) => Math.abs(item.offsetLeft - this.rail.scrollLeft) < Math.abs(this.items[best].offsetLeft - this.rail.scrollLeft) ? i : best, 0);
        const next = (nearest + 1) % this.items.length;
        this.rail.scrollTo({ left: this.items[next].offsetLeft, behavior: 'smooth' });
        this.schedule();
      }, 5200);
    }
    disconnectedCallback() { clearTimeout(this.timer); this.controller?.abort(); this.observer?.disconnect(); }
  }
  if (!customElements.get('pj-trust-strip')) customElements.define('pj-trust-strip', PjTrustStrip);

  const revealObserver = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('pj-visible');
      revealObserver.unobserve(entry.target);
    });
  }, { threshold: 0.05, rootMargin: '0px 0px 30px 0px' });
  const initReveals = (root = document) => {
    root.querySelectorAll('[data-pj-reveal]').forEach(el => {
      if (el.dataset.pjReady) return;
      el.dataset.pjReady = 'true';
      if (editor || reduced.matches || el.closest('[data-motion="false"]') || el.getBoundingClientRect().top < window.innerHeight) {
        el.classList.add('pj-visible');
        return;
      }
      el.classList.add('pj-reveal-ready');
      revealObserver.observe(el);
    });
  };
  initReveals();
  document.addEventListener('shopify:section:load', event => initReveals(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-pj-reveal]').forEach(el => revealObserver.unobserve(el)));
  reduced.addEventListener('change', () => { if (reduced.matches) document.querySelectorAll('[data-pj-reveal]').forEach(el => el.classList.add('pj-visible')); });

  let driftFrame;
  const drift = () => {
    driftFrame = null;
    if (reduced.matches || editor || window.innerWidth < 768) return;
    document.querySelectorAll('[data-pj-drift]').forEach(el => {
      if (el.closest('[data-motion="false"]')) return;
      const rect = el.parentElement.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return;
      const offset = ((rect.top + rect.height / 2) / window.innerHeight - 0.5) * -28;
      el.style.transform = `translateY(${Math.max(-24, Math.min(24, offset))}px)`;
    });
  };
  window.addEventListener('scroll', () => { if (!driftFrame) driftFrame = requestAnimationFrame(drift); }, { passive: true });
})();
